import type { FC } from 'react';
import { features } from '@app/utils/feature-flags';
import { usePrototypeSettingsStore } from '@data/storage';

import { useHasMountedPrototype } from '../../stores/prototype-mount-store';

const RIBBON_LABEL = 'PROTOTYPE MODE';

interface PrototypeModeIndicatorProps {
  label?: string;
}

/**
 * Renders a corner ribbon + dashed inset border around the viewport while
 * any `<PrototypeBoundary>` is mounted (i.e. an actual prototype is on
 * screen). The master switch still gates it overall, but the indicator
 * stays hidden on non-prototype pages. Rendered once at the app shell —
 * keeps `pointer-events-none` so it never blocks interactions.
 *
 * Inline `transform: rotate(45deg)` is the one project-style exception called
 * out in `apps/web/AGENTS.md` (no `<style>` tag): the diagonal ribbon position
 * cannot be expressed cleanly with utilities here.
 */
export const PrototypeModeIndicator: FC<PrototypeModeIndicatorProps> = ({
  label = RIBBON_LABEL,
}) => {
  const hasMountedPrototype = useHasMountedPrototype();
  const isCanvasVisible = usePrototypeSettingsStore((s) => s.isCanvasVisible);
  if (!features.prototypesEnabled) return null;
  if (!isCanvasVisible) return null;
  if (!hasMountedPrototype) return null;

  return (
    <aside
      aria-label="Prototype mode is active"
      data-testid="prototype-mode-indicator"
      className="pointer-events-none fixed inset-0 z-[9999]"
    >
      <div
        aria-hidden="true"
        className="absolute inset-[8px] rounded-[14px] border-2 border-dashed border-amber-600 sm:inset-[5px]"
      />
      <div
        aria-hidden="true"
        className="absolute right-[-48px] top-[32px] w-[190px] bg-gradient-to-br from-amber-400 to-amber-600 py-[7px] text-center text-[11px] font-black uppercase tracking-[0.1em] text-amber-950 shadow-lg sm:right-[-54px] sm:top-[16px] sm:text-[9px]"
        style={{ transform: 'rotate(45deg)' }}
      >
        {label}
      </div>
      <span className="sr-only">Prototype mode is active.</span>
    </aside>
  );
};
